import { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import {
  LayoutDashboard, MessageSquare, Target, Building2, Wrench, Truck, Megaphone,
  CalendarDays, LifeBuoy, Package, Settings, type LucideIcon,
} from 'lucide-react';
import { useChannelCounts } from '../../modules/communicatie/hooks/useCommunicatie.js';
import { useSocketEvent } from '../shared/hooks/useSocket.js';
import { useAuth } from '../auth/AuthContext.js';
import { toonInMenu } from './rolProfiel.js';

interface SidebarItem {
  icon: LucideIcon;
  label: string;
  path: string;
  match?: string;
  badgeKey?: string;
}

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: counts } = useChannelCounts();

  // Track unseen logistiek updates via Socket.io — reset when navigating to logistiek
  const [logistiekBadge, setLogistiekBadge] = useState(0);
  const handleLogistiekEvent = useCallback(() => {
    if (!location.pathname.startsWith('/logistiek')) {
      setLogistiekBadge((prev) => prev + 1);
    }
  }, [location.pathname]);
  useSocketEvent('logistiek:update', handleLogistiekEvent);

  // Reset badge when user navigates to logistiek
  useEffect(() => {
    if (location.pathname.startsWith('/logistiek')) {
      setLogistiekBadge(0);
    }
  }, [location.pathname]);

  const { user } = useAuth();
  const mainItems: SidebarItem[] = useMemo(() => [
    { icon: LayoutDashboard, label: 'Dashboard',    path: '/dashboard',     match: '/dashboard' },
    { icon: MessageSquare,   label: 'Communicatie', path: '/communicatie',  match: '/communicatie', badgeKey: 'total' },
    { icon: Target,          label: 'CRM',          path: '/crm',           match: '/crm' },
    { icon: Building2,       label: 'Projecten',    path: '/projecten',     match: '/projecten' },
    { icon: Wrench,          label: 'Buitendienst', path: '/buitendienst',  match: '/buitendienst' },
    { icon: Truck,           label: 'Logistiek',    path: '/logistiek',     match: '/logistiek', badgeKey: 'logistiek' },
    { icon: Megaphone,       label: 'Marketing',    path: '/marketing',     match: '/marketing' },
    { icon: CalendarDays,    label: 'Planning',     path: '/planning',      match: '/planning' },
    { icon: LifeBuoy,        label: 'Service',      path: '/service',       match: '/service' },
    { icon: Package,         label: 'Catalogus',    path: '/catalogus',     match: '/catalogus' },
  ].filter((i) => toonInMenu(user?.role, i.path)), [user?.role]);

  const bottomItems: SidebarItem[] = useMemo(() => [
    { icon: Settings,        label: 'Admin',        path: '/settings',      match: '/settings' },
  ], []);

  const isActive = (item: SidebarItem) => {
    if (!item.match) return location.pathname === item.path;
    return location.pathname.startsWith(item.match);
  };

  const getBadgeCount = (item: SidebarItem): number => {
    if (!item.badgeKey) return 0;
    if (item.badgeKey === 'logistiek') return logistiekBadge;
    if (!counts) return 0;
    return (counts as any)[item.badgeKey] || 0;
  };

  const renderItem = (item: SidebarItem) => {
    const active = isActive(item);
    const badgeCount = getBadgeCount(item);
    const Icon = item.icon;
    // Selectie = zacht merkvlak + merkkleur + zichtbare indicator links (niet enkel kleur).
    return (
      <button
        key={item.path}
        onClick={() => navigate(item.path)}
        aria-current={active ? 'page' : undefined}
        className={clsx(
          'relative w-[76px] min-h-[56px] rounded-lg flex flex-col items-center justify-center gap-1 py-1.5 transition-colors',
          active
            ? 'bg-brand-light text-brand'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
        )}
      >
        {active && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r bg-brand" aria-hidden="true" />}
        <Icon size={22} strokeWidth={1.75} aria-hidden="true" />
        <span className={clsx('text-[11px] leading-tight', active ? 'font-semibold' : 'font-medium')}>
          {item.label}
        </span>
        {badgeCount > 0 && (
          <span
            className={clsx(
              'absolute top-1 right-2 bg-status-red text-white text-[10px] font-semibold min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1',
              item.badgeKey === 'logistiek' && 'animate-pulse'
            )}
            aria-label={`${badgeCount} nieuw`}
          >
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        )}
      </button>
    );
  };

  return (
    <aside aria-label="Hoofdnavigatie" className="hidden md:flex w-[88px] h-full bg-card border-r border-card-border flex-col items-center py-3 gap-1 flex-shrink-0 overflow-y-auto">
      {mainItems.map(renderItem)}

      <div className="flex-1" />

      <div className="w-10 border-t border-card-border my-1" aria-hidden="true" />

      {bottomItems.map(renderItem)}
    </aside>
  );
}
